import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "../../../Services/Global";
import { useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import logger from '../../../Services/LoggingService';
import { getUnitConfigurationFromConfigurationKey } from "../../../Utils/UnitConfigurationHelper";
import { createFlexStyles } from '../../../theme/layoutStyles';

// Option item interface for dropdown selections
interface SelectOption {
  key: string;
  text: string;
}

// Component props interface
interface PositionSelectionProps {
  onChange: (position: string) => void;
  onIncompleteUnitConfiguration?: () => void;
  /**
   * Reports whether the initial configuration tree is still loading so the
   * parent view can coordinate a single, unified loading sequence
   * (configuration -> filters -> list) instead of rendering multiple
   * competing spinners on top of each other.
   */
  onConfigLoadingChange?: (loading: boolean) => void;
}

export const PositionSelection: React.FC<PositionSelectionProps> = ({
  onChange,
  onIncompleteUnitConfiguration,
  onConfigLoadingChange,
}) => {
  const ctx = useMsal();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);

  // Loading states. `isLoading` starts `true` because the configuration
  // tree fetch is triggered immediately on mount; starting `false` caused
  // a brief flash of empty UI before the first render of the spinner.
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPositions, setIsLoadingPositions] = useState(false);
  const [isLoadingDisplay, setIsLoadingDisplay] = useState(true);

  // Guards the one-shot propagation of the restored localStorage selection
  // after the initial configuration load so the parent can start fetching
  // the sample list only once – in deterministic order after the filters.
  const didBroadcastInitialSelectionRef = useRef(false);

  // Selected values
  const [selectedIndustry, setSelectedIndustry] = useState(
    getLocalStorageItem("position_industry") as string
  );
  const [selectedSubIndustry, setSelectedSubIndustry] = useState(
    getLocalStorageItem("position_subindustry") as string
  );
  const [selectedApplication, setSelectedApplication] = useState(
    getLocalStorageItem("position_application") as string
  );
  const [selectedSubApplication, setSelectedSubApplication] = useState(
    getLocalStorageItem("position_subapplication") as string
  );
  const [selectedProcessStage, setSelectedProcessStage] = useState(
    getLocalStorageItem("position_processstage") as string
  );
  const [selectedProductLine, setSelectedProductLine] = useState(
    getLocalStorageItem("position_productline") as string
  );
  const [selectedAlfaLavalPosition, setSelectedAlfaLavalPosition] = useState(
    getLocalStorageItem("position_alfalavalposition") as string
  );

  // All available options
  const [availableIndustries, setAvailableIndustries] = useState<SelectOption[]>([]);
  const [availableSubIndustries, setAvailableSubIndustries] = useState<SelectOption[]>([]);
  const [availableApplications, setAvailableApplications] = useState<SelectOption[]>([]);
  const [availableSubApplications, setAvailableSubApplications] = useState<SelectOption[]>([]);
  const [availableProductLines, setAvailableProductLines] = useState<SelectOption[]>([]);

  // Filtered options for current view
  const [industries, setIndustries] = useState<SelectOption[]>([]);
  const [subIndustries, setSubIndustries] = useState<SelectOption[]>([]);
  const [applications, setApplications] = useState<SelectOption[]>([]);
  const [subApplications, setSubApplications] = useState<SelectOption[]>([]);
  const [productLines, setProductLines] = useState<SelectOption[]>([]);
  
  // Position options
  const [processStages, setProcessStages] = useState<string[]>([]);
  const [alfaLavalPositions, setAlfaLavalPositions] = useState<string[]>([]);

  // When isLoading changes, update isLoadingDisplay with a delay
  useEffect(() => {
    if (isLoading) {
      setIsLoadingDisplay(true);
      return; // No cleanup needed when setting loading to true
    } else {
      // Add a small delay before hiding loading to prevent flicker
      const timer = setTimeout(() => {
        setIsLoadingDisplay(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Report initial-config loading state to the parent view so it can render
  // a single unified spinner. We keep firing on every change (parent uses a
  // stable setState setter so re-renders do not loop).
  useEffect(() => {
    onConfigLoadingChange?.(isLoading);
  }, [isLoading, onConfigLoadingChange]);

  // Fetch the initial configuration tree exactly once on mount. After it
  // resolves, if a complete selection was restored from localStorage we
  // emit `onChange` a single time – this is what makes the sample list
  // start loading only AFTER the filters are ready to render.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await getUnitConfiguration();
      if (cancelled) return;

      if (!didBroadcastInitialSelectionRef.current && selectedAlfaLavalPosition) {
        didBroadcastInitialSelectionRef.current = true;
        onChange(selectedAlfaLavalPosition);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load process stages whenever the product line changes.
  useEffect(() => {
    if (!selectedProductLine) return;
    getProcessStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductLine]);

  // Load Alfa Laval positions whenever the process stage changes. Previously
  // this endpoint was hit twice on mount because the same call was also made
  // from the product-line effect above – the duplicate has been removed.
  useEffect(() => {
    if (!selectedProcessStage) return;
    getAlfaLavalPositionsAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcessStage]);

  // Validate selections after options are loaded
  useEffect(() => {
    if (industries.length > 0) {
      validateAndCleanSelections();
    }
  }, [industries, subIndustries, applications, subApplications, productLines]);

  /**
   * Helper function to safely get valid Select values
   */
  const getValidSelectValue = (selectedValue: string, options: SelectOption[]): string => {
    if (!selectedValue) return "";
    return options.find(opt => opt.key === selectedValue) ? selectedValue : "";
  };

  /**
   * Validates and cleans up selected values based on available options
   */
  const validateAndCleanSelections = () => {
    // Validate industry
    if (selectedIndustry && !industries.find(opt => opt.key === selectedIndustry)) {
      setSelectedIndustry("");
      setLocalStorageItem("position_industry", "");
    }
    
    // Validate sub industry
    if (selectedSubIndustry && !subIndustries.find(opt => opt.key === selectedSubIndustry)) {
      setSelectedSubIndustry("");
      setLocalStorageItem("position_subindustry", "");
    }
    
    // Validate application
    if (selectedApplication && !applications.find(opt => opt.key === selectedApplication)) {
      setSelectedApplication("");
      setLocalStorageItem("position_application", "");
    }
    
    // Validate sub application
    if (selectedSubApplication && !subApplications.find(opt => opt.key === selectedSubApplication)) {
      setSelectedSubApplication("");
      setLocalStorageItem("position_subapplication", "");
    }
    
    // Validate product line
    if (selectedProductLine && !productLines.find(opt => opt.key === selectedProductLine)) {
      setSelectedProductLine("");
      setLocalStorageItem("position_productline", "");
    }
  };

  /**
   * Fetches the unit configuration hierarchy from the API
   */
  async function getUnitConfiguration() {
    setIsLoading(true);
    try {
      const response = await apiService.getAsync(
        `settings/configurationnavigations`
      );
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const unitConfigurations: any[] = await response.json();

      let inds: SelectOption[] = [];
      let subInds: SelectOption[] = [];
      let apps: SelectOption[] = [];
      let subApps: SelectOption[] = [];
      let lines: SelectOption[] = [];

      unitConfigurations.forEach((industry) => {
        inds.push({
          key: industry.name,
          text: industry.name,
        });

        industry.children?.forEach((subIndustry: any) => {
          subInds.push({
            key: `${industry.name}-${subIndustry.name}`,
            text: subIndustry.name,
          });

          subIndustry.children?.forEach((application: any) => {
            apps.push({
              key: `${industry.name}-${subIndustry.name}-${application.name}`,
              text: application.name,
            });

            application.children?.forEach((subApplication: any) => {
              subApps.push({
                key: `${industry.name}-${subIndustry.name}-${application.name}-${subApplication.name}`,
                text: subApplication.name,
              });

              subApplication.children?.forEach((prodLine: any) => {
                lines.push({
                  key: `${industry.name}-${subIndustry.name}-${application.name}-${subApplication.name}-${prodLine.name}`,
                  text: prodLine.name,
                });
              });
            });
          });
        });
      });

      // Store all available options
      setAvailableIndustries(inds);
      setAvailableSubIndustries(subInds);
      setAvailableApplications(apps);
      setAvailableSubApplications(subApps);
      setAvailableProductLines(lines);

      // Set current options
      setIndustries(inds);
      
      // Set filtered options based on current selections
      setSubIndustries(subInds.filter(x => x.key.includes(selectedIndustry)));
      setApplications(apps.filter(x => x.key.includes(selectedSubIndustry)));
      setSubApplications(subApps.filter(x => x.key.includes(selectedApplication)));
      setProductLines(lines.filter(x => x.key.includes(selectedSubApplication)));
      
    } catch (error) {
      logger.error("Failed to load unit configuration", error as Error, null, {
        component: 'PositionSelection',
        method: 'getUnitConfiguration'
      });
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Fetches process stages for the selected product line
   */
  async function getProcessStages() {
    if (!selectedProductLine) return;

    setIsLoadingPositions(true);
    try {
      const uc = getUnitConfigurationFromConfigurationKey(selectedProductLine);
      const response = await apiService.getAsync(
        "settings/selections/processStages",
        uc
      );

      if (!response.ok) {
        setProcessStages([]);
        throw new Error("Could not get process stages");
      }

      const result: string[] = await response.json();
      setProcessStages(result);
    } catch (error) {
      logger.error("Failed to load process stages", error as Error, {
        productLine: selectedProductLine
      }, {
        component: 'PositionSelection',
        method: 'getProcessStages'
      });
      setProcessStages([]);
    } finally {
      setIsLoadingPositions(false);
    }
  }

  /**
   * Fetches Alfa Laval positions for the selected process stage
   */
  async function getAlfaLavalPositionsAsync() {
    if (!selectedProcessStage || !selectedProductLine) return;

    setIsLoadingPositions(true);
    try {
      const uc = getUnitConfigurationFromConfigurationKey(selectedProcessStage);
      const response = await apiService.getAsync(
        "settings/selections/positions",
        uc
      );

      if (!response.ok) {
        setAlfaLavalPositions([]);
        throw new Error("Could not get positions");
      }

      const result: string[] = await response.json();
      setAlfaLavalPositions(result);
    } catch (error) {
      logger.error("Failed to load Alfa Laval positions", error as Error, {
        productLine: selectedProductLine,
        processStage: selectedProcessStage
      }, {
        component: 'PositionSelection',
        method: 'getAlfaLavalPositionsAsync'
      });
      setAlfaLavalPositions([]);
    } finally {
      setIsLoadingPositions(false);
    }
  }

  /**
   * Handles selection changes for dropdown selects
   */
  const handleSelectChange = (
    event: SelectChangeEvent<string>,
    level: string
  ) => {
    const value = event.target.value;
    
    switch (level) {
      case "industry":
        setSelectedIndustry(value);
        setSubIndustries(availableSubIndustries.filter(x => x.key.includes(value)));
        setSelectedSubIndustry("");
        setSelectedApplication("");
        setSelectedSubApplication("");
        setSelectedProcessStage("");
        setSelectedAlfaLavalPosition("");
        setSelectedProductLine("");
        setAlfaLavalPositions([]);
        setProcessStages([]);
        setLocalStorageItem("position_industry", value);
        onIncompleteUnitConfiguration?.();
        break;
      case "subIndustry":
        setSelectedSubIndustry(value);
        setApplications(availableApplications.filter(x => x.key.includes(value)));
        setSelectedApplication("");
        setSelectedSubApplication("");
        setSelectedProcessStage("");
        setSelectedAlfaLavalPosition("");
        setSelectedProductLine("");
        setAlfaLavalPositions([]);
        setProcessStages([]);
        setLocalStorageItem("position_subindustry", value);
        onIncompleteUnitConfiguration?.();
        break;
      case "application":
        setSelectedApplication(value);
        setSubApplications(availableSubApplications.filter(x => x.key.includes(value)));
        setSelectedSubApplication("");
        setSelectedProcessStage("");
        setSelectedAlfaLavalPosition("");
        setSelectedProductLine("");
        setAlfaLavalPositions([]);
        setProcessStages([]);
        setLocalStorageItem("position_application", value);
        onIncompleteUnitConfiguration?.();
        break;
      case "subApplication":
        setSelectedSubApplication(value);
        setProductLines(availableProductLines.filter(x => x.key.includes(value)));
        setSelectedProcessStage("");
        setSelectedAlfaLavalPosition("");
        setSelectedProductLine("");
        setAlfaLavalPositions([]);
        setProcessStages([]);
        setLocalStorageItem("position_subapplication", value);
        onIncompleteUnitConfiguration?.();
        break;
      case "productLine":
        setSelectedProductLine(value);
        setSelectedAlfaLavalPosition("");
        setAlfaLavalPositions([]);
        setProcessStages([]);
        setLocalStorageItem("position_productline", value);
        onIncompleteUnitConfiguration?.();
        break;
    }
  };

  /**
   * Handles button selection for process stages and positions
   */
  const handleButtonSelection = (value: string, level: string) => {
    if (level === "processStage") {
      setSelectedProcessStage(value);
      if (value !== selectedProcessStage) {
        setAlfaLavalPositions([]);
        setSelectedAlfaLavalPosition("");
      }
      setLocalStorageItem("position_processstage", value);
      onIncompleteUnitConfiguration?.();
    } else if (level === "alfaLavalPosition") {
      setSelectedAlfaLavalPosition(value);
      setLocalStorageItem("position_alfalavalposition", value);
      onChange(value);
    }
  };

  /**
   * Clears all selections and localStorage values
   */
  const clearAllSelections = () => {
    // Clear all state variables
    setSelectedIndustry("");
    setSelectedSubIndustry("");
    setSelectedApplication("");
    setSelectedSubApplication("");
    setSelectedProcessStage("");
    setSelectedProductLine("");
    setSelectedAlfaLavalPosition("");
    
    // Clear arrays
    setProcessStages([]);
    setAlfaLavalPositions([]);

    // Clear localStorage items
    setLocalStorageItem("position_industry", "");
    setLocalStorageItem("position_subindustry", "");
    setLocalStorageItem("position_application", "");
    setLocalStorageItem("position_subapplication", "");
    setLocalStorageItem("position_processstage", "");
    setLocalStorageItem("position_productline", "");
    setLocalStorageItem("position_alfalavalposition", "");

    // Notify parent that configuration is incomplete
    onIncompleteUnitConfiguration?.();
  };

  /**
   * Loading indicator component
   */
  const LoadingIndicator: React.FC = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 200,
        width: "100%",
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body1" ml={2}>
        Loading configuration...
      </Typography>
    </Box>
  );

  // If data is loading, show loading spinner
  if (isLoadingDisplay && !industries.length) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ padding: 3, marginTop: 2 }}>
      <Stack spacing={3} sx={{ width: "100%", maxWidth: "98%", mx: "auto" }}>
        {/* Main selection form */}
        <Paper
          elevation={2}
          sx={{
            padding: 3,
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[2],
            marginBottom: 3,
            width: "100%",
          }}
        >
          <Paper sx={{ border: "none", boxShadow: "none" }}>
            {/* Title */}
            <Box sx={{ ...flexStyles.flexCenterBetween, mb: "30px" }}>
              <Typography variant="body1" fontWeight="bold" sx={{ marginLeft: 2.5 }}>
                Select targeted position
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small"
                onClick={clearAllSelections}
                sx={{ 
                  textTransform: "none",
                  fontWeight: "medium"
                }}
              >
                Clear
              </Button>
            </Box>
            {/* First row */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                marginBottom: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Industry
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={getValidSelectValue(selectedIndustry, industries)}
                    onChange={(e) => handleSelectChange(e, "industry")}
                    disabled={isLoading}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) return <em>Select industry</em>;
                      const option = industries.find(opt => opt.key === selected);
                      return option ? option.text : selected;
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select industry</em>
                    </MenuItem>
                    {industries.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.text}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {selectedIndustry && (
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Sub Industry
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getValidSelectValue(selectedSubIndustry, subIndustries)}
                      onChange={(e) => handleSelectChange(e, "subIndustry")}
                      disabled={isLoading}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em>Select sub industry</em>;
                        const option = subIndustries.find(opt => opt.key === selected);
                        return option ? option.text : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select sub industry</em>
                      </MenuItem>
                      {subIndustries.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {selectedSubIndustry && (
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Application
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getValidSelectValue(selectedApplication, applications)}
                      onChange={(e) => handleSelectChange(e, "application")}
                      disabled={isLoading}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em>Select application</em>;
                        const option = applications.find(opt => opt.key === selected);
                        return option ? option.text : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select application</em>
                      </MenuItem>
                      {applications.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {selectedApplication && (
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Sub application
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getValidSelectValue(selectedSubApplication, subApplications)}
                      onChange={(e) => handleSelectChange(e, "subApplication")}
                      disabled={isLoading}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em>Select sub application</em>;
                        const option = subApplications.find(opt => opt.key === selected);
                        return option ? option.text : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select sub application</em>
                      </MenuItem>
                      {subApplications.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {selectedApplication && selectedSubApplication && (
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    fontWeight="bold"
                  >
                    Product line
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={getValidSelectValue(selectedProductLine, productLines)}
                      onChange={(e) => handleSelectChange(e, "productLine")}
                      disabled={isLoading}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <em>Select product line</em>;
                        const option = productLines.find(opt => opt.key === selected);
                        return option ? option.text : selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select product line</em>
                      </MenuItem>
                      {productLines.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Process Stages */}
          {processStages.length > 0 && (
            <Paper sx={{ border: "none", boxShadow: "none" }}>
              <Divider />
              <Typography variant="body1" fontWeight="bold" mb="30px" mt="60px">
                Process stage
              </Typography>
              {isLoadingPositions && processStages.length === 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", m: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading process stages...
                  </Typography>
                </Box>
              ) : (
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ flexWrap: "wrap", gap: 1, my: 2 }}
                >
                  {processStages.map((stage, index) => {
                    const stageKey = `${selectedProductLine}-${stage}`;
                    const isSelected = selectedProcessStage === stageKey;

                    return (
                      <Button
                        key={`${stage}-${index}`}
                        variant={isSelected ? "contained" : "outlined"}
                        color="primary"
                        disabled={isLoading}
                        onClick={() =>
                          handleButtonSelection(stageKey, "processStage")
                        }
                        sx={{
                          minWidth: "48px",
                          minHeight: "40px",
                          textTransform: "none",
                          fontWeight: "bold",
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        {stage}
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          )}

          {/* Alfa Laval Positions */}
          {alfaLavalPositions.length > 0 && (
            <Paper sx={{ border: "none", boxShadow: "none" }}>
              <Divider />
              <Typography variant="body1" fontWeight="bold" mb="30px" mt="60px">
                Positions
              </Typography>
              {isLoadingPositions ? (
                <Box sx={{ display: "flex", alignItems: "center", m: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading positions...
                  </Typography>
                </Box>
              ) : (
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ flexWrap: "wrap", gap: 1, my: 2 }}
                >
                  {alfaLavalPositions.map((pos, index) => {
                    const posKey = `${selectedProcessStage}-${pos}`;
                    const isSelected = selectedAlfaLavalPosition === posKey;

                    return (
                      <Button
                        key={`${pos}-${index}`}
                        variant={isSelected ? "contained" : "outlined"}
                        color="primary"
                        disabled={isLoading}
                        onClick={() => {
                          handleButtonSelection(posKey, "alfaLavalPosition");
                        }}
                        sx={{
                          minWidth: "120px",
                          minHeight: "40px",
                          textTransform: "none",
                          fontWeight: "bold",
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        {pos}
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};
